/**
 * Sentry Configuration for LEMO Frontend
 * Real-time error monitoring and performance tracking
 */
import * as Sentry from '@sentry/react';

export const initSentry = () => {
  const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
  
  if (!sentryDsn) {
    console.warn('⚠️ SENTRY_DSN not configured - Error monitoring disabled');
    return false;
  }
  
  Sentry.init({
    dsn: sentryDsn,
    
    // Environment
    environment: process.env.NODE_ENV,
    
    // Release version
    release: process.env.REACT_APP_VERSION || 'lemo-frontend@1.0.0',
    
    // Performance monitoring
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
    
    // Session replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    
    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    
    // Filter events
    beforeSend(event, hint) {
      // Don't send errors from localhost in production
      if (process.env.NODE_ENV === 'production' && 
          event.request?.url?.includes('localhost')) {
        return null;
      }
      return event;
    },
  });
  
  console.log('✅ Sentry initialized - Error monitoring enabled');
  return true;
};

/**
 * Capture an error with additional context
 */
export const captureError = (error, context = {}) => {
  if (context.userId) {
    Sentry.setUser({ id: context.userId });
  }
  
  if (context.tags) {
    Object.entries(context.tags).forEach(([key, value]) => {
      Sentry.setTag(key, value);
    });
  }
  
  Sentry.captureException(error);
};

/**
 * Capture a message with level
 */
export const captureMessage = (message, level = 'info', context = {}) => {
  if (context.tags) {
    Object.entries(context.tags).forEach(([key, value]) => {
      Sentry.setTag(key, value);
    });
  }
  
  Sentry.captureMessage(message, level);
};

/**
 * Set user context for error tracking
 */
export const setUser = (user) => {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.name,
    });
  } else {
    Sentry.setUser(null);
  }
};

/**
 * Add a breadcrumb for debugging
 */
export const addBreadcrumb = (message, category = 'action', data = {}) => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
};

export default Sentry;
